import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Image, 
  Video, 
  File, 
  X, 
  ArrowLeft, 
  ArrowRight,
  Upload,
  AlertTriangle
} from 'lucide-react';
import { usePostingStore, PlatformId } from '../../lib/store/usePostingStore';

const Step2MediaOptions: React.FC = () => {
  const { 
    files, 
    platforms, 
    titles, 
    descriptions,
    setTitle,
    setDescription,
    setThumbnail,
    thumbnails,
    nextStep,
    prevStep
  } = usePostingStore();
  
  const [activeTab, setActiveTab] = useState<PlatformId | null>(
    platforms.find(p => p.enabled)?.id || null
  );
  
  const enabledPlatforms = platforms.filter(p => p.enabled);
  
  // If no platforms are enabled, redirect back to step 1
  if (enabledPlatforms.length === 0) {
    prevStep();
    return null;
  }
  
  const handleThumbnailUpload = (e: React.ChangeEvent<HTMLInputElement>, platformId: PlatformId) => {
    const file = e.target.files?.[0];
    if (file) {
      setThumbnail(platformId, file);
    }
  };
  
  const removeThumbnail = (platformId: PlatformId) => {
    setThumbnail(platformId, null);
  };
  
  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) {
      return <Image className="h-5 w-5 text-blue-500" />;
    } else if (file.type.startsWith('video/')) {
      return <Video className="h-5 w-5 text-purple-500" />;
    } else {
      return <File className="h-5 w-5 text-gray-500" />;
    }
  };
  
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
  
  const getPlatformConfig = (platformId: PlatformId) => {
    return platforms.find(p => p.id === platformId);
  };
  
  const renderPlatformOptions = (platformId: PlatformId) => {
    const platform = getPlatformConfig(platformId);
    if (!platform) return null;
    
    const needsTitle = platform.characterLimits.title !== undefined;
    const needsDescription = platform.characterLimits.description !== undefined;
    const needsThumbnail = platformId === 'yt' || platformId === 'ig';
    
    return (
      <div className="space-y-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
          {platform.name} Options
        </h3>
        
        {/* Title Field (for YouTube) */}
        {needsTitle && (
          <div>
            <div className="flex justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Title
              </label>
              <span className={`text-xs ${
                titles[platformId]?.length > (platform.characterLimits.title || 0)
                  ? 'text-red-500'
                  : 'text-gray-500 dark:text-gray-400'
              }`}>
                {titles[platformId]?.length || 0}/{platform.characterLimits.title}
              </span>
            </div>
            <input
              type="text"
              value={titles[platformId] || ''}
              onChange={(e) => setTitle(platformId, e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white ${
                titles[platformId]?.length > (platform.characterLimits.title || 0)
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                  : 'border-gray-300 dark:border-dark-600'
              }`}
              placeholder={`Enter ${platform.name} title...`}
            />
          </div>
        )}
        
        {/* Description Field (for YouTube) */}
        {needsDescription && (
          <div>
            <div className="flex justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Description
              </label>
              <span className={`text-xs ${
                descriptions[platformId]?.length > (platform.characterLimits.description || 0)
                  ? 'text-red-500'
                  : 'text-gray-500 dark:text-gray-400'
              }`}>
                {descriptions[platformId]?.length || 0}/{platform.characterLimits.description}
              </span>
            </div>
            <textarea
              value={descriptions[platformId] || ''}
              onChange={(e) => setDescription(platformId, e.target.value)}
              rows={4}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white ${
                descriptions[platformId]?.length > (platform.characterLimits.description || 0)
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                  : 'border-gray-300 dark:border-dark-600'
              }`}
              placeholder={`Enter ${platform.name} description...`}
            />
          </div>
        )}
        
        {/* Thumbnail Upload (for YouTube and Instagram) */}
        {needsThumbnail && (
          <div className="mt-8">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {platformId === 'yt' ? 'Video Thumbnail' : 'Cover Image'}
            </label>
            
            {thumbnails[platformId] ? (
              <div className="relative mt-2">
                <img
                  src={URL.createObjectURL(thumbnails[platformId]!)}
                  alt="Thumbnail"
                  className="w-full h-40 object-cover rounded-lg"
                />
                <button
                  onClick={() => removeThumbnail(platformId)}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 dark:border-dark-600 rounded-lg p-8 flex flex-col items-center justify-center mt-2">
                <div className="flex flex-col items-center justify-center text-center">
                  <Upload className="h-10 w-10 text-gray-400 dark:text-gray-500 mb-4" />
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                    Upload a thumbnail image
                  </p>
                  <label className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors cursor-pointer">
                    <span>Select Image</span>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => handleThumbnailUpload(e, platformId)}
                    />
                  </label>
                </div>
              </div>
            )}
            
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              {platformId === 'yt' 
                ? 'Recommended size: 1280x720 pixels (16:9)' 
                : 'Recommended size: 1080x1080 pixels (1:1)'}
            </p>
          </div>
        )}
      </div>
    );
  };
  
  return (
    <div className="space-y-8">
      {/* Files Preview */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
          Media Files
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {files.map((file, index) => (
            <div
              key={index}
              className="bg-gray-50 dark:bg-dark-700 rounded-lg p-4 flex items-start"
            >
              <div className="flex items-center space-x-3">
                {getFileIcon(file)}
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-[200px]">
                    {file.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {formatFileSize(file.size)}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Platform Tabs */}
      <div className="border-b border-gray-200 dark:border-dark-700">
        <nav className="-mb-px flex space-x-8">
          {enabledPlatforms.map((platform) => (
            <button
              key={platform.id}
              onClick={() => setActiveTab(platform.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === platform.id
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-gray-600'
              }`}
            >
              {platform.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Platform-specific Options */}
      {activeTab && renderPlatformOptions(activeTab)}

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-6">
        <button
          onClick={prevStep}
          className="px-4 py-2 border border-gray-300 dark:border-dark-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
        >
          <div className="flex items-center">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </div>
        </button>
        <button
          onClick={nextStep}
          className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
        >
          Continue to Captions
        </button>
      </div>
    </div>
  );
};

export default Step2MediaOptions;